import { Icon } from '@iconify/react'
import { useCallback, useEffect, useState } from 'react'

import { useNotification } from 'app/providers'
import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import type { EncounterCheckoutCreditCard } from 'features/Procedure/state/procedure-api.type'
import { SettingsService } from 'features/SettingsOld/services/settingsService'

import styles from './cc-on-file-row.module.scss'

const { formatNumber, formatExpiryDate, formatCVC } = SettingsService

export type CcOnFileValues = {
  cc_name: string
  cc_number: string
  cc_expiration: string
  cc_cvv: string
  cc_zip: string
}

type Props = {
  initial: EncounterCheckoutCreditCard | null | undefined
  values: CcOnFileValues
  onChange: (next: CcOnFileValues) => void
  onSave: (
    values: CcOnFileValues,
  ) => Promise<{ success: boolean; error?: string; message?: string }>
  disabled?: boolean
}

const mapInitial = (cc: EncounterCheckoutCreditCard | null | undefined): CcOnFileValues => ({
  cc_name: cc?.cc_name ?? '',
  cc_number: cc?.cc ?? '',
  cc_expiration: cc?.cc_expiration ?? '',
  cc_cvv: cc?.cc_cvv ?? '',
  cc_zip: cc?.cc_zip ?? '',
})

const copyToClipboard = async (value: string): Promise<void> => {
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
  } catch {
    // clipboard write may be denied; silently ignore — user can still copy manually
  }
}

export const CcOnFileRow = ({ initial, values, onChange, onSave, disabled }: Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const { setNotification } = useNotification()

  useEffect(() => {
    onChange(mapInitial(initial))
    // re-sync only when the underlying patient/encounter CC-on-file changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.cc_name, initial?.cc, initial?.cc_expiration, initial?.cc_cvv, initial?.cc_zip])

  const setField = useCallback(
    (field: keyof CcOnFileValues, transform?: (raw: string) => string) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value
        const next = transform ? transform(raw) : raw
        onChange({ ...values, [field]: next })
      },
    [values, onChange],
  )

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      const result = await onSave(values)
      if (result.success) {
        setNotification({
          type: ALERT_CONSTANTS.SUCCESS,
          title: 'CC on file saved',
          description: result.message ?? 'Card details updated',
          duration: 5,
        })
      } else {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          title: 'Save failed',
          description: result.error ?? 'Save failed',
        })
      }
    } finally {
      setIsSaving(false)
    }
  }, [onSave, setNotification, values])

  return (
    <div className={styles.row}>
      <div className={styles.labelRow}>
        <span>CC on file:</span>
        <span className={styles.hint}>
          (click on the <Icon icon='ph:copy' /> and then paste the data in the row below)
        </span>
      </div>

      <div className={styles.fields}>
        <span className={styles.fieldGroup}>
          <Input
            name='ccof_name'
            placeholder='Name'
            value={values.cc_name}
            parentClassName={styles.inputWrap}
            disabled={disabled}
            onChange={setField('cc_name')}
          />
        </span>
        <span className={styles.fieldGroup}>
          <Input
            name='ccof_number'
            placeholder='Card Number'
            value={values.cc_number}
            parentClassName={styles.inputWrap}
            disabled={disabled}
            onChange={setField('cc_number', formatNumber)}
          />
          <button
            type='button'
            className={styles.copyBtn}
            aria-label='Copy card number'
            onClick={() => copyToClipboard(values.cc_number)}>
            <Icon icon='ph:copy' />
          </button>
        </span>
        <span className={styles.fieldGroup}>
          <Input
            name='ccof_expiration'
            placeholder='MM/YY'
            value={values.cc_expiration}
            parentClassName={styles.inputWrap}
            disabled={disabled}
            onChange={setField('cc_expiration', formatExpiryDate)}
          />
          <button
            type='button'
            className={styles.copyBtn}
            aria-label='Copy expiration'
            onClick={() => copyToClipboard(values.cc_expiration)}>
            <Icon icon='ph:copy' />
          </button>
        </span>
        <span className={styles.fieldGroup}>
          <Input
            name='ccof_cvv'
            placeholder='CVV'
            value={values.cc_cvv}
            parentClassName={styles.inputWrap}
            disabled={disabled}
            onChange={setField('cc_cvv', formatCVC)}
          />
          <button
            type='button'
            className={styles.copyBtn}
            aria-label='Copy CVV'
            onClick={() => copyToClipboard(values.cc_cvv)}>
            <Icon icon='ph:copy' />
          </button>
        </span>
        <span className={styles.fieldGroup}>
          <Input
            name='ccof_zip'
            placeholder='ZIP'
            value={values.cc_zip}
            parentClassName={styles.inputWrap}
            disabled={disabled}
            onChange={setField('cc_zip')}
          />
          <button
            type='button'
            className={styles.copyBtn}
            aria-label='Copy ZIP'
            onClick={() => copyToClipboard(values.cc_zip)}>
            <Icon icon='ph:copy' />
          </button>
        </span>
        <Button
          proportion={BUTTON_PROPORTION.SMALL}
          disabled={disabled || isSaving}
          onClick={handleSave}>
          Save CC
        </Button>
      </div>
    </div>
  )
}
